import { useState } from "react";
import { createPortal } from "react-dom";
import { putLockCompany, putUnlockCompany } from "@/services/Company";
import { enableKycScreening } from "@/services/inviteCompany";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiErrorCode, getApiErrorStatus } from "@/utils/apiError";
import { Grid, Stack } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Company } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import Text from "@/components/atoms/Text";
import Checkbox from "@/components/atoms/Checkbox";
import CTA from "@/components/atoms/CTA";
import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";

interface CompanyDrawerProps {
  company: Company;
}

export const getUnlockErrorKey = (error: unknown) =>
  getApiErrorCode(error) === "USR-252"
    ? "notifications:lockCompany.awaitingApproval"
    : "notifications:lockCompany.companyUnlockError";

export default function SettingsTab({ company }: CompanyDrawerProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: lockCompany } = useMutation({
    mutationFn: (companyId: number) => putLockCompany(companyId),
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:lockCompany.companyLocked"))
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
    },
    onError: (error) => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:lockCompany.companyLockError"))
      );
    },
  });

  const { mutateAsync: unlockCompany } = useMutation({
    mutationFn: (companyId: number) => putUnlockCompany(companyId),
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:lockCompany.companyUnlocked"))
      );
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
    },
    onError: (error) => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t(getUnlockErrorKey(error))));
    },
  });

  return (
    <Grid p="lg" align="center">
      <Grid.Col span={3}>
        <Text color="secondary" size="sm">
          {t("commonAdmin:tradeLock")}
        </Text>
      </Grid.Col>
      <Grid.Col span={1}>
        <Checkbox checked={company.isLocked} />
      </Grid.Col>
      <Grid.Col span={8}>
        <CTA onClick={() => (company.isLocked ? unlockCompany(company.id) : lockCompany(company.id))}>
          {company.isLocked ? t("commonAdmin:lockCompany.unlockCompany") : t("commonAdmin:lockCompany.lockCompany")}
        </CTA>
      </Grid.Col>

      <KycScreeningSetting company={company} />
    </Grid>
  );
}

const KycScreeningSetting = ({ company }: { company: Company }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isCurrentCompanyAdmin, isUserAdmin } = useAuth();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { mutate: enableScreening, isPending } = useMutation({
    mutationFn: () => enableKycScreening(company.id),
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:kycScreening.enabled"))
      );
    },
    onError: (error) => {
      // The flag is committed before D&B is called, so a D&B failure still leaves screening enabled
      // and the company is picked up by the next periodic run.
      const code = getApiErrorCode(error);
      const status = getApiErrorStatus(error);
      const messageKey =
        code === "USR-239"
          ? "notifications:kycScreening.dunsMissing"
          : status === 403
            ? "notifications:kycScreening.forbidden"
            : "notifications:kycScreening.screeningFailed";
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t(messageKey)));
    },
    onSettled: () => {
      setConfirmModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
      queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] });
    },
  });

  if (!isCurrentCompanyAdmin || !isUserAdmin) return null;

  return (
    <>
      {createPortal(
        <Modal
          opened={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          title={t("commonAdmin:kycScreening.confirmTitle")}
        >
          <ModalContent>
            <Stack gap="md" p="20px 23px">
              <Text>{t("commonAdmin:kycScreening.confirmContent")}</Text>
            </Stack>
          </ModalContent>
          <ModalCTAs>
            <CTA intent="secondary" onClick={() => setConfirmModalOpen(false)} disabled={isPending}>
              {t("actions:cancel")}
            </CTA>
            <CTA onClick={() => enableScreening()} loading={isPending} data-testid="confirm-enable-kyc-btn">
              {t("commonAdmin:kycScreening.enable")}
            </CTA>
          </ModalCTAs>
        </Modal>,
        document.body
      )}

      <Grid.Col span={3}>
        <Text color="secondary" size="sm">
          {t("commonAdmin:kycScreening.label")}
        </Text>
      </Grid.Col>
      <Grid.Col span={1}>
        <Checkbox checked={company.kycScreeningEnabled} readOnly />
      </Grid.Col>
      <Grid.Col span={8}>
        <CTA
          onClick={() => setConfirmModalOpen(true)}
          disabled={company.kycScreeningEnabled}
          data-testid="enable-kyc-btn"
        >
          {t("commonAdmin:kycScreening.enable")}
        </CTA>
      </Grid.Col>
    </>
  );
};
